import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, FileDown } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import type { BonusHistory } from "@/types/hr";
import { exportBonusToPDF } from "@/lib/pdfExport";

interface EmployeeBonusTableProps {
  employeeId: string;
  employeeName?: string;
  readOnly?: boolean;
  canViewData?: boolean;
}

export function EmployeeBonusTable({ employeeId, employeeName = "Mitarbeiter", readOnly = false, canViewData: canViewDataProp }: EmployeeBonusTableProps) {
  const { user, profile, userRole } = useAuth();
  
  // Berechne canViewData intern wenn nicht explizit gesetzt
  const isAdmin = userRole === 'admin';
  const isOwnProfile = profile?.employee_id === employeeId;
  const canViewData = canViewDataProp !== undefined ? canViewDataProp : (isAdmin || isOwnProfile);
  const [bonusHistory, setBonusHistory] = useState<BonusHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<BonusHistory>>({
    currency: "EUR",
  });

  useEffect(() => {
    if (user && employeeId) {
      loadBonusHistory();
    }
  }, [user, employeeId]);

  const loadBonusHistory = async () => {
    try {
      const { data, error } = await supabase
        .from("bonus_history")
        .select("*")
        .eq("employee_id", employeeId)
        .order("bonus_date", { ascending: false });

      if (error) throw error;
      setBonusHistory(data || []);
    } catch (error) {
      console.error("Error loading bonus history:", error);
      toast.error("Fehler beim Laden der Bonushistorie");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingId(null);
    setFormData({
      currency: "EUR",
    });
    setDialogOpen(true);
  };

  const handleEdit = (entry: BonusHistory) => {
    setEditingId(entry.id);
    setFormData(entry);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.bonus_date || !formData.amount) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const { error } = await supabase
          .from("bonus_history")
          .update({
            bonus_date: formData.bonus_date,
            amount: formData.amount,
            currency: formData.currency || "EUR",
            notes: formData.notes,
          })
          .eq("id", editingId);

        if (error) throw error;
        toast.success("Bonuseintrag erfolgreich aktualisiert");
      } else {
        const { error } = await supabase
          .from("bonus_history")
          .insert([{
            employee_id: employeeId,
            bonus_date: formData.bonus_date!,
            amount: formData.amount!,
            currency: formData.currency || "EUR",
            bonus_type: "performance",
            notes: formData.notes,
            created_by: user!.id,
          }]);

        if (error) throw error;
        toast.success("Bonuseintrag erfolgreich gespeichert");
      }

      setDialogOpen(false);
      loadBonusHistory();
    } catch (error) {
      console.error("Error saving bonus history:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Möchten Sie diesen Eintrag wirklich löschen?")) return;

    try {
      const { error } = await supabase
        .from("bonus_history")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Eintrag gelöscht");
      loadBonusHistory();
    } catch (error) {
      console.error("Error deleting bonus history:", error);
      toast.error("Fehler beim Löschen");
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Bonushistorie</CardTitle>
              <CardDescription>Verwalten Sie Boni und Sonderzahlungen</CardDescription>
            </div>
            <div className="flex gap-2">
              {canViewData && (
                <Button variant="outline" onClick={() => exportBonusToPDF(employeeName, bonusHistory)}>
                  <FileDown className="mr-2 h-4 w-4" />
                  PDF
                </Button>
              )}
              {!readOnly && (
                <Button onClick={handleAdd}>
                  <Plus className="mr-2 h-4 w-4" />
                  Neuer Bonus
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {bonusHistory.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[100px]">Datum</TableHead>
                  <TableHead className="min-w-[120px]">Betrag</TableHead>
                  <TableHead className="min-w-[250px]">Begründung</TableHead>
                  {!readOnly && <TableHead className="min-w-[80px]"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {bonusHistory.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      {format(new Date(entry.bonus_date), "dd.MM.yyyy", { locale: de })}
                    </TableCell>
                    <TableCell>
                      {canViewData ? (
                        entry.amount.toLocaleString("de-DE", {
                          style: "currency",
                          currency: entry.currency,
                        })
                      ) : (
                        <span className="text-muted-foreground italic">Vertraulich</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[250px] truncate">{entry.notes || "-"}</TableCell>
                    {!readOnly && (
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(entry)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(entry.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Noch keine Bonuseinträge vorhanden
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Bonus bearbeiten" : "Bonus hinzufügen"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bonusdatum *</Label>
                <Input
                  type="date"
                  value={formData.bonus_date || ""}
                  onChange={(e) => setFormData({ ...formData, bonus_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Betrag *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount || ""}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                  placeholder="z.B. 1000"
                />
              </div>
            </div>

            <div>
              <Label>Begründung</Label>
              <Textarea
                rows={3}
                value={formData.notes || ""}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="z.B. Leistungsbonus für Q4 2024, Projektabschlussbonus, etc."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
